import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Globe, Users, BookOpen, LineChart } from 'lucide-react';
import { Link } from 'react-router-dom';

// Components
import Hero from '../components/Home/Hero';
import FeaturedPrograms from '../components/Home/FeaturedPrograms';
import LatestUpdates from '../components/Home/LatestUpdates';
import PartnerShowcase from '../components/Home/PartnerShowcase';

export default function Home() {
  const handleScrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <Hero />
      
      {/* Overview Section */}
      <section className="py-20 bg-gradient-to-br from-green-50 to-emerald-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative bg-white rounded-2xl shadow-xl overflow-hidden mb-16"
          >
            {/* Decorative elements */}
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-green-500 to-emerald-500" />
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-green-100 to-emerald-100 rounded-full -translate-y-1/2 translate-x-1/2 opacity-20" />
            <div className="absolute bottom-0 left-0 w-40 h-40 bg-gradient-to-tr from-green-100 to-emerald-100 rounded-full translate-y-1/2 -translate-x-1/2 opacity-20" />
            
            {/* Content */}
            <div className="relative p-8 md:p-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center">
                CLIMATE FINANCE AND SUSTAINABILITY CENTRE
              </h2>
              <div className="max-w-4xl mx-auto">
                <p className="text-lg text-gray-600 leading-relaxed">
                  The Climate Finance and Sustainability Centre (CFSC) stands as one of the first 
                  southern-driven centres of excellence on climate finance, committed to building 
                  long-term, impactful skills across the African continent. With robust outreach 
                  and strategic networks encompassing academia, government, and the private sector, 
                  the Centre harnesses the power of climate and sustainable finance to transform 
                  education.
                </p>
                <p className="text-lg text-gray-600 leading-relaxed mt-6">
                  Its primary mission is to equip the next generation—researchers, 
                  policymakers, practitioners, and industry leaders—with the essential knowledge 
                  and tools to tackle the pressing challenges of climate change. Through cutting-edge 
                  research, targeted training, and capacity-building initiatives, the CFSC is poised 
                  to drive sustainable development and foster economic resilience in Uganda, the 
                  region and Africa.
                </p>
                <div className="mt-8 flex justify-center">
                  <Link
                    to="/about#top"
                    onClick={handleScrollToTop}
                    className="inline-flex items-center px-6 py-3 bg-green-600 text-white rounded-full hover:bg-green-700 transition-colors group"
                  >
                    About Us
                    <ArrowRight className="ml-2 h-5 w-5 transform group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: BookOpen,
                title: "Expanding Access to Training",
                description: "Providing high-quality education in climate finance and sustainability to African learners who face barriers to international education"
              },
              {
                icon: Globe,
                title: "Africa-Centered Research",
                description: "Creating a platform for research and knowledge-sharing tailored to Africa's specific climate challenges"
              },
              {
                icon: Users,
                title: "Policy Engagement",
                description: "Supporting African policymakers with evidence-based insights to shape informed climate finance policies"
              }
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="bg-green-100 p-3 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                  <item.icon className="h-6 w-6 text-green-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Programs Section */}
      <FeaturedPrograms />

      {/* Latest Updates Section */}
      <LatestUpdates />

      {/* Partners Section */}
      <PartnerShowcase />

      {/* Call to Action Section */}
      <section className="py-20 bg-gradient-to-br from-green-900 to-emerald-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="text-4xl font-bold text-white mb-6">
              Join Us in Shaping Africa's Sustainable Future
            </h2>
            <p className="text-xl text-green-100 mb-8">
              Together, we can create impactful solutions for climate resilience and 
              sustainable development across Africa.
            </p>
            <Link
              to="/contact#top"
              onClick={handleScrollToTop}
              className="inline-flex items-center px-8 py-4 bg-white text-green-900 rounded-full hover:bg-green-50 transition-colors"
            >
              Get Involved
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}